'use client';
import type { TopNavigationIconButtonType } from '@yanolja-rnd/nol-design-system';
import type { DeviceType, ProductUnitItem } from '@weberus/shared-types';
import { cn, FloatingButton, TopNavigation, showToast } from '@yanolja-rnd/nol-design-system';
import { type FC, type MouseEvent, useEffect, useRef, useState } from 'react';
import { IcClose } from '@yanolja-rnd/nol-icons';
// eslint-disable-next-line react/no-deprecated
import { render } from 'react-dom';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { convertToProductUnitProps } from '@/app/_components/feature/product-unit/converter';
import { ProductUnitCard } from '@/app/_components/feature/product-unit';
import { useUniversalLocalMapQuery } from '@/app/(pages)/results/api/queries';
import { Failover } from '@/app/_components/common/empty-state';

declare global {
  interface Window {
    handleInfoWindowClick?: (e: MouseEvent<HTMLAnchorElement>, index: number) => void;
  }
}

export interface LocalMapLayoutProps {
  deviceType?: DeviceType;
  onClose: (isClosed: boolean) => void;
  onImpression: () => void;
  onImpressionPlace?: (place: ProductUnitItem) => void;
  onClickPlace?: (place: ProductUnitItem) => void;
  onClickMoreButton?: (serverLogMeta: Record<string, unknown> | null | undefined) => void;
}

type MarkerState = 'default' | 'selected' | 'disabled';

const baseClass = 'w-fit text-nowrap rounded-full px-10 py-7 relative flex-shrink-0 text-center';
const disabledClass = `${baseClass} border-[1px] text-text-neutral-sub !typography-body-12-regular border-line-neutral-weak3 bg-static-white hover-never:bg-interaction-neutral-presshover-weak1 hover-never:border-interaction-neutral-disable-main`;
const selectedClass = `${baseClass} border-[1px] text-static-white typography-body-12-bold border-static-black bg-static-black text-static-white`;
const defaultClass = `${baseClass} border-[1px] text-text-neutral-main typography-body-12-bold border-line-neutral-weak3 bg-static-white hover-never:bg-interaction-neutral-presshover-weak1 hover-never:border-interaction-neutral-disable-main`;

export const LocalMapLayout: FC<LocalMapLayoutProps> = ({
  deviceType = 'etc',
  onClose,
  onImpression,
  onImpressionPlace,
  onClickPlace,
  onClickMoreButton,
}: LocalMapLayoutProps) => {
  const { paging, placeList, fetchNextPage, isFetchingNextPage, isError, isFetchNextPageError, refetch } =
    useUniversalLocalMapQuery();

  const isMounted = useRef(false);
  const mapRef = useRef<naver.maps.Map | null>(null);
  const markersRef = useRef<naver.maps.Marker[]>([]);
  const selectedMarkerRef = useRef<naver.maps.Marker | null>(null);
  const infoWindowsRef = useRef<naver.maps.InfoWindow[]>([]);
  const selectedIndexRef = useRef<number | null>(null); // MW POI 카드 대응을 위해 선언, 이벤트 리스너 내에서 참조하기 위해 선언
  const mapEventListenersRef = useRef<naver.maps.MapEventListener[]>([]);
  const placeListRef = useRef(placeList);
  const [selectedPlace, setSelectedPlace] = useState<ProductUnitItem | null>(null);
  const hasNextPage = (paging?.total ?? 0) > 1;
  const DEFAULT_ZOOM_LEVEL = 14;
  const DEFAULT_FOCUS_DURATION = 200;

  const [mapOption, setMapOption] = useState<naver.maps.MapOptions>({
    zoom: DEFAULT_ZOOM_LEVEL,
    tileDuration: 300,
    baseTileOpacity: 1,
    tileSpare: 7,
  });

  const leftButton: TopNavigationIconButtonType = {
    'aria-label': 'Close',
    icon: <IcClose />,
    onClick: () => {
      onClose(true);
    },
  };

  const handleCardImpression = (item: ProductUnitItem) => {
    onImpressionPlace?.(item);
  };

  const handleCardClick = (item: ProductUnitItem) => {
    onClickPlace?.(item);
    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: item.data.action.app });
      return;
    }
    window.open(item.data.action.web);
  };

  const handleUserEventListener = () => {
    const closeInfoWindowsHandler = () => {
      if (selectedMarkerRef.current === null) return;

      // PC
      if (infoWindowsRef.current) {
        infoWindowsRef.current.forEach((infoWindow) => {
          if (infoWindow?.getMap()) {
            infoWindow.close();
          }
        });
      }

      const place = placeListRef.current[selectedIndexRef.current ?? 0];
      // 유저 액션에 의해 infoWindow 가 닫히는 경우, marker 원래 상태로 복원
      unSelectedMarker(place, selectedMarkerRef.current);
    };

    const eventListeners: naver.maps.MapEventListener[] = [];
    const mapEvents = ['dragstart', 'click', 'touchstart'];
    mapEvents.forEach((eventType) => {
      const listener = naver.maps.Event.addListener(mapRef.current, eventType, closeInfoWindowsHandler);
      eventListeners.push(listener);
    });
    mapEventListenersRef.current = eventListeners;
  };

  // 마커를 클릭했을 때 POI 카드와 관련된 처리를 수행.
  const handleClickMarker = (index: number) => {
    handleUserEventListener();
    const place = placeList[index];
    if (deviceType === 'etc') {
      if (markersRef && infoWindowsRef.current) {
        if (infoWindowsRef.current.length && infoWindowsRef.current[index]?.getMap()) {
          selectedIndexRef.current = null;
          infoWindowsRef.current[index].close();
        } else {
          selectedIndexRef.current = index;
          const markerInfoEl = document.querySelector('#markerInfo');
          render(
            <ProductUnitCard
              key={place.data.id}
              url={place.data.action.web}
              onImpression={() => handleCardImpression(place)}
              onItemClick={() => handleCardClick(place)}
              {...convertToProductUnitProps(place)}
            />,
            document.getElementById('markerInfo'),
          );

          infoWindowsRef.current[index] = new naver.maps.InfoWindow({
            content: `
          <div style="cursor: pointer;"
              onclick="window.handleInfoWindowClick(event, ${index})">
            ${markerInfoEl?.innerHTML || ''}
          </div>
        `,
            borderWidth: 0,
            backgroundColor: 'transparent',
            anchorSkew: false,
            disableAnchor: false,
            anchorSize: new naver.maps.Size(0, 0),
            pixelOffset: new naver.maps.Point(40, -8),
          });

          window.handleInfoWindowClick = (e: MouseEvent<HTMLAnchorElement>, clickedIndex: number) => {
            e.preventDefault();
            handleCardClick(placeList[clickedIndex]);
          };

          infoWindowsRef.current[index].open(mapRef.current!, markersRef.current[index]);
        }
      }
    } else {
      // MW 환경에서 poi 카드가 열려있는 경우
      if (selectedIndexRef.current === index) {
        selectedIndexRef.current = null;
        unSelectedMarker(place, selectedMarkerRef.current);
      } else {
        selectedIndexRef.current = index;
        selectedMarker(place, markersRef.current[index]);
      }
    }
    handleCardImpression(place);
  };

  const markerHtmlByState = (title: string, state: MarkerState) => {
    let className = defaultClass;

    if (state === 'selected') {
      className = selectedClass;
    } else if (state === 'disabled') {
      className = disabledClass;
    }

    return `
      <div class="relative flex flex-col items-center">
        <div class="${className}">
          ${title}
        </div>
      </div>
    `;
  };

  const makeMarkers = (placeList: ProductUnitItem[]) => {
    return placeList.map((place: ProductUnitItem, index: number) => {
      const marker = new naver.maps.Marker({
        map: mapRef.current ?? undefined,
        position: new naver.maps.LatLng(place?.marker?.position.latitude || 0, place?.marker?.position.longitude || 0),
        icon: {
          content: markerHtmlByState(place?.marker?.title || '', place?.marker?.disabled ? 'disabled' : 'default'),
        },
        clickable: true,
      });
      marker.addListener('click', () => {
        // 이미 선택 되어진 marker 와 같다면, unSelected 처리
        if (selectedMarkerRef.current === marker) {
          unSelectedMarker(place, marker);
        } else {
          // 선택되어진 marker 가 있다면, unSelected 처리
          if (selectedIndexRef.current) {
            const prevSelectedPlace = placeList[selectedIndexRef.current ?? 0];
            unSelectedMarker(prevSelectedPlace, selectedMarkerRef.current);
          }
          selectedMarker(place, marker);
        }
        handleClickMarker(index);
      });
      return marker;
    });
  };

  const unSelectedMarker = (place: ProductUnitItem | null, marker: naver.maps.Marker | null) => {
    selectedMarkerRef.current = null;
    selectedIndexRef.current = null;
    setSelectedPlace(null);
    marker?.setIcon({
      content: markerHtmlByState(place?.marker?.title || '', place?.marker?.disabled ? 'disabled' : 'default'),
    });
  };

  const selectedMarker = (place: ProductUnitItem, marker: naver.maps.Marker) => {
    selectedMarkerRef.current = marker;
    setSelectedPlace(place);
    marker?.setIcon({
      content: markerHtmlByState(place?.marker?.title || '', 'selected'),
    });

    if (mapRef.current) {
      const newCenter = new naver.maps.LatLng(
        place.marker?.position?.latitude || 0,
        place.marker?.position?.longitude || 0,
      );
      mapRef.current.panTo(newCenter, { duration: DEFAULT_FOCUS_DURATION, easing: 'easeOutCubic' });
    }
  };

  const handleClickMoreButton = () => {
    if (!isFetchingNextPage && !paging?.isLast) {
      fetchNextPage();
      onClickMoreButton?.(placeList[0].serverLogMeta);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      onImpression();
      isMounted.current = true;
      return;
    }

    return () => {
      if (mapEventListenersRef.current.length > 0) {
        mapEventListenersRef.current.forEach((listener) => {
          naver.maps.Event.removeListener(listener);
        });
        mapEventListenersRef.current = [];
      }
      if (window.handleInfoWindowClick) {
        delete window.handleInfoWindowClick;
      }
    };
  }, []);

  useEffect(() => {
    if (isError || isFetchNextPageError) {
      showToast({ children: '화면을 불러오지 못했어요. 다시 시도해 주세요.' });
    }
  }, [isError, isFetchNextPageError]);

  useEffect(() => {
    if (!window?.naver) {
      return;
    }

    if (placeList && placeList.length > 0) {
      placeListRef.current = placeList;

      if (!mapRef.current) {
        const firstPlace = placeList[0];
        if (firstPlace?.marker?.position) {
          // list 내 첫번째 data 로 지도를 centering 한다.
          const { latitude, longitude } = firstPlace.marker.position;
          const newCenter = new naver.maps.LatLng(latitude, longitude);
          const updatedMapOption = {
            ...mapOption,
            center: newCenter,
          };
          mapRef.current = new naver.maps.Map('naver-map', updatedMapOption);
          setMapOption(updatedMapOption);

          const m = makeMarkers(placeList);
          markersRef.current = m;
        }
      } else if (mapRef.current) {
        markersRef.current.forEach((marker) => marker?.setMap(null));
        markersRef.current = [];

        const m = makeMarkers(placeList);
        markersRef.current = m;

        if (typeof selectedIndexRef.current === 'number') {
          const selectedPlace = placeList[selectedIndexRef.current];
          selectedMarker(selectedPlace, markersRef.current[selectedIndexRef.current]);
        }
      }
    }
  }, [placeList]);

  return (
    <section className="flex h-screen w-screen flex-col items-center overflow-y-visible bg-static-white">
      {/* Header */}
      <div
        className={cn(
          'sticky top-0 z-20 flex w-full justify-center border-b-line-neutral-weak2',
          deviceType === 'etc' ? 'border-b-[1px]' : 'shadow-1',
        )}>
        <div className="w-full max-w-legacy-pc-size">
          <TopNavigation deviceType={deviceType} leftButton={leftButton} title="지도" pcType="pcLegacySize" />
        </div>
      </div>
      {/* Body */}
      <div className="relative h-full w-full max-w-legacy-pc-size">
        {!isError ? (
          <>
            {hasNextPage && (
              <div className="absolute left-1/2 z-10 mt-10 flex -translate-x-1/2 transform">
                <FloatingButton
                  buttonType="capsule"
                  direction="right"
                  size="small"
                  disabled={paging?.isLast}
                  text={
                    paging?.current && paging?.total ? `숙소 더보기 ${paging.current} / ${paging.total}` : '숙소 더보기'
                  }
                  onClick={handleClickMoreButton}
                />
              </div>
            )}
            {/* pc 용 infoWindow */}
            <div id="markerInfo" className="hidden" />
            {/* 지도 */}
            <div id="naver-map" className="absolute h-full w-full" />
            {/* mobile 용 하단 고정 infoWindow */}
            {deviceType !== 'etc' && selectedPlace && (
              <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pb-[env(safe-area-inset-bottom)]">
                <div className="flex w-full justify-center px-16">
                  <ProductUnitCard
                    key={selectedPlace.data.id}
                    url={selectedPlace.data.action.web}
                    onImpression={() => handleCardImpression(selectedPlace)}
                    onItemClick={() => handleCardClick(selectedPlace)}
                    {...convertToProductUnitProps(selectedPlace)}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex w-full items-center justify-center bg-static-white">
            <Failover refetch={refetch} />
          </div>
        )}
      </div>
    </section>
  );
};
